import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, message, Popconfirm, Typography, Tag, Input, Select, Modal, Form, InputNumber, Upload } from 'antd';
import { PlusOutlined, DownloadOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../api';
import dayjs from 'dayjs';
import { exportSalesOrderToExcel } from '../../utils/exportSalesOrder';

const { Title } = Typography;
const { Option } = Select;

  const SalesOrders = () => {
  const location = useLocation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [search, setSearch] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [paymentForm] = Form.useForm();
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentFileList, setPaymentFileList] = useState([]);
  const [currentOrderId, setCurrentOrderId] = useState(null);
  const [currentOrder, setCurrentOrder] = useState(null);
  const navigate = useNavigate();

  const statusMap = {
    pending: { text: '待发货', color: 'orange' },
    completed: { text: '已发货', color: 'green' },
    cancelled: { text: '已取消', color: 'red' },
  }

  const fetchOrders = useCallback(async (page = 1, pageSize = 10, searchTerm = '', paymentStatus = '') => {
    setLoading(true);
    try {
      const response = await api.get('/v1/sales_orders', {
        params: { page, pageSize, search: searchTerm, payment_status: paymentStatus },
      });
      setData(response.data.data.map(item => ({ ...item, key: item.id })));
      setPagination({
        current: page,
        pageSize: pageSize,
        total: response.data.total,
      });
    } catch (error) {
      message.error('获取销售订单列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders(pagination.current, pagination.pageSize, search, paymentStatusFilter);
  }, [fetchOrders]);

  useEffect(() => {
    // Check if navigation state contains payment status filter
    if (location.state?.paymentStatus) {
      setPaymentStatusFilter(location.state.paymentStatus);
      fetchOrders(1, pagination.pageSize, search, location.state.paymentStatus);
      // Clear the location state after applying it
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleTableChange = (newPagination) => {
    fetchOrders(newPagination.current, newPagination.pageSize, search, paymentStatusFilter);
  };

  const handleSearch = (value) => {
    setSearch(value);
    fetchOrders(1, pagination.pageSize, value, paymentStatusFilter);
  };

  const handlePaymentStatusChange = (value) => {
    setPaymentStatusFilter(value);
    fetchOrders(1, pagination.pageSize, search, value);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/v1/sales_orders/${id}`);
      message.success('销售订单删除成功');
      fetchOrders(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error('删除失败');
    }
  };

  const getPaymentStatus = (paidAmount, totalAmount) => {
    if (!paidAmount || paidAmount === 0) return '未付款';
    if (paidAmount >= totalAmount) return '已付款';
    return '部分付款';
  };

  const handlePayment = (record) => {
    setCurrentOrderId(record.id);
    setCurrentOrder(record);
    setPaymentModalVisible(true);
    setPaymentFileList([]);
    paymentForm.resetFields();
  };

  const handleExport = async (orderId) => {
    try {
      await exportSalesOrderToExcel(orderId, api);
      message.success('导出成功');
    } catch (error) {
      message.error('导出失败');
    }
  };



  const handlePaymentCancel = () => {
    setPaymentModalVisible(false);
    setPaymentFileList([]);
    paymentForm.resetFields();
    setCurrentOrderId(null);
    setCurrentOrder(null);
  };

  

  const handlePaymentOk = async () => {
    try {
      const values = await paymentForm.validateFields();
      setPaymentLoading(true);

      const payload = {
        amount: values.amount,
        attachment_urls: paymentFileList.map(file => file.path || file.url),
      };

      await api.post(`/v1/sales_orders/${currentOrderId}/payment`, payload);
      message.success('收款成功');
      setPaymentModalVisible(false);
      setPaymentFileList([]);
      paymentForm.resetFields();
      fetchOrders(pagination.current, pagination.pageSize, search, paymentStatusFilter);
    } catch (error) {
      message.error(error.response?.data?.error || '收款失败');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handlePaymentPreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      });
    }
    window.open(file.url || file.preview, '_blank');
  };

  const handlePaymentFileChange = ({ fileList: newFileList }) => setPaymentFileList(newFileList);

  const handlePaymentUpload = async ({ file, onSuccess, onError }) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('bucket', 'sales-order-attachments');

    try {
      const response = await api.post('/v1/attachments/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      file.url = response.data.url;
      file.path = response.data.path;
      onSuccess(response.data, file);
      message.success(`${file.name} 上传成功`);
    } catch (error) {
      onError(error);
      message.error(`${file.name} 上传失败`);
    }
  };

  const handlePaymentFileRemove = async (file) => {
    const fileUrl = file.url;
    if (fileUrl) {
      try {
        await api.delete('/v1/attachments', { data: { url: fileUrl, bucket: 'sales-order-attachments' } });
        message.success(`附件删除成功`);
        const newFileList = paymentFileList.filter(item => item.uid !== file.uid);
        setPaymentFileList(newFileList);
        return true;
      } catch (error) {
        message.error(`附件删除失败: ${error.response?.data?.error || '服务器错误'}`);
        return Promise.reject(false);
      }
    }
    const newFileList = paymentFileList.filter(item => item.uid !== file.uid);
    setPaymentFileList(newFileList);
    return true;
  };

  const columns = [
    { title: '订单号', dataIndex: 'order_no', key: 'order_no' },
    { 
      title: '客户', 
      key: 'customer_info',
      render: (_, record) => {
        const company = record.customer?.company_name || 'N/A';
        const contact = record.customer?.contact_person;
        return (
          <div>
            <div>{company}</div>
            {contact && <div style={{ fontSize: '12px', color: '#999' }}>{contact}</div>}
          </div>
        );
      }
    },
    { title: '订单日期', dataIndex: 'order_date', key: 'order_date', render: (text) => dayjs(text).format('YYYY-MM-DD') },
    { title: '总金额', dataIndex: 'total_amount', key: 'total_amount', render: (amount) => `¥ ${amount.toFixed(2)}` },
    {
      title: '收款状态',
      dataIndex: 'paid_amount',
      key: 'paid_amount',
      render: (paidAmount, record) => {
        const paymentStatus = getPaymentStatus(paidAmount, record.total_amount);
        let color = '#faad14';
        if (paymentStatus === '已付款') color = '#52c41a';
        if (paymentStatus === '未付款') color = '#ff4d4f';
        if (record.status === 'cancelled') color = '#999';
        return <span style={{ color }}>{`${paymentStatus}|¥ ${paidAmount.toFixed(2)}`}</span>;
      }
    },
    {
      title: '发货状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusConfig = statusMap[status] || { text: status, color: 'default' };
        if (status === 'cancelled') {
          return <span style={{ color: '#999' }}>{statusConfig.text}</span>;
        }
        return <Tag color={statusConfig.color}>{statusConfig.text}</Tag>;
      }
    },
    { title: '备注', dataIndex: 'note', key: 'note' },
    { title: '更新时间', dataIndex: 'updated_at', key: 'updated_at' },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => {
        const isCompleted = record.status === 'completed';
        const isCancelled = record.status === 'cancelled';
        const paymentStatus = getPaymentStatus(record.paid_amount, record.total_amount);
        const isUnpaidOrPartial = paymentStatus === '未付款' || paymentStatus === '部分付款';
        return (
          <Space size="small" style={{ marginLeft: -8 }}>
            <Button type="link" size="small" onClick={() => navigate(`/sales/sales-orders/${record.id}`)}>
              {isCompleted || isCancelled ? '查看' : '编辑'}
            </Button>
            {isUnpaidOrPartial && !isCancelled && (
              <Button type="link" size="small" onClick={() => handlePayment(record)}>收款</Button>
            )}
            {!isCancelled && (
              <Button type="link" size="small" onClick={() => navigate('/financial-records', { state: { search: record.order_no } })}>
                收款记录
              </Button>
            )}
            <Button 
              type="link" 
              size="small" 
              icon={<DownloadOutlined />}
              onClick={() => handleExport(record.id)}
            >
              导出
            </Button>
            {!isCompleted && (
              <Popconfirm title="确定删除此订单吗？" onConfirm={() => handleDelete(record.id)}>
                <Button type="link" size="small" danger>删除</Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>销售订单管理</Title>
        <Space>
          <Input.Search
            placeholder="搜索订单号或备注"
            onSearch={handleSearch}
            style={{ width: 240 }}
            allowClear
          />
          <Select
            placeholder="收款状态"
            value={paymentStatusFilter || undefined}
            onChange={handlePaymentStatusChange}
            style={{ width: 120 }}
            allowClear
          >
            <Option value="pending">待收款</Option>
            <Option value="paid">已收款</Option>
          </Select>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/sales/sales-orders/new')}>
            新增订单
          </Button>
        </Space>
      </div>
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        bordered
        size="small"
      />

      <Modal
        title="收款"
        open={paymentModalVisible}
        onOk={handlePaymentOk}
        onCancel={handlePaymentCancel}
        confirmLoading={paymentLoading}
        width={600}
      >
        {currentOrder && (
          <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
            <div style={{ marginBottom: 8 }}>
              <span style={{ color: '#666' }}>订单总金额：</span>
              <span style={{ fontWeight: 'bold', marginLeft: 8 }}>¥ {currentOrder.total_amount.toFixed(2)}</span>
            </div>
            <div style={{ marginBottom: 8 }}>
              <span style={{ color: '#666' }}>已收款金额：</span>
              <span style={{ fontWeight: 'bold', marginLeft: 8, color: '#52c41a' }}>¥ {(currentOrder.paid_amount || 0).toFixed(2)}</span>
            </div>
            <div>
              <span style={{ color: '#666' }}>待收款金额：</span>
              <span style={{ fontWeight: 'bold', marginLeft: 8, color: '#ff4d4f' }}>¥ {(currentOrder.total_amount - (currentOrder.paid_amount || 0)).toFixed(2)}</span>
            </div>
          </div>
        )}
        <Form form={paymentForm} layout="vertical">
          <Form.Item
            name="amount"
            label="收款金额"
            rules={[
              { required: true, message: '请输入收款金额' },
              { type: 'number', min: 0.01, message: '收款金额必须大于0' },
              {
                validator: (_, value) => {
                  if (currentOrder && value > currentOrder.total_amount - (currentOrder.paid_amount || 0)) {
                    return Promise.reject(new Error('收款金额不能超过待收款金额'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <InputNumber
              placeholder={`请输入收款金额（最多 ¥ ${(currentOrder?.total_amount - (currentOrder?.paid_amount || 0)).toFixed(2)}）`}
              min={0.01}
              max={currentOrder ? currentOrder.total_amount - (currentOrder.paid_amount || 0) : undefined}
              precision={2}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item label="附件">
            <Upload
              customRequest={handlePaymentUpload}
              fileList={paymentFileList}
              onPreview={handlePaymentPreview}
              onChange={handlePaymentFileChange}
              onRemove={handlePaymentFileRemove}
              listType="picture-card"
            >
              {paymentFileList.length < 5 && <div><PlusOutlined /><div style={{ marginTop: 8 }}>上传</div></div>}
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SalesOrders;
